import React, { useEffect, useState, useCallback, useMemo } from "react";
import { AlertTriangle, AlertCircle, Info, Check, CheckCheck, Trash2 } from "lucide-react";
import {
  fetchAlertsFeed,
  acknowledgeAlert,
  resolveAlert,
  deleteAlerts,
} from "@/services/alerts";
import { FilterSelect, FilterSelectItem } from "@/components/ui/filter-select";
import { useConfirm } from "@/components/ui/confirm-dialog";

const SEVERITY_META = {
  critical: { label: "Critical", icon: AlertCircle, className: "text-[var(--severity-critical-text,#b91c1c)] bg-[var(--severity-critical-bg,#fee2e2)]" },
  warning:  { label: "Warning",  icon: AlertTriangle, className: "text-[var(--severity-warning-text,#92400e)] bg-[var(--severity-warning-bg,#fef3c7)]" },
  info:     { label: "Info",     icon: Info, className: "text-[var(--severity-info-text,#1e40af)] bg-[var(--severity-info-bg,#dbeafe)]" },
};

const STATUS_TABS = [
  { id: "new",          label: "New" },
  { id: "acknowledged", label: "Acknowledged" },
  { id: "resolved",     label: "Resolved" },
  { id: "all",          label: "All" },
];

function formatTime(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  const diffMs = Date.now() - d.getTime();
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  return d.toLocaleDateString();
}

function SeverityPill({ severity }) {
  const meta = SEVERITY_META[severity] || SEVERITY_META.info;
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${meta.className}`}>
      <Icon size={12} strokeWidth={2.5} />
      {meta.label}
    </span>
  );
}

const CHECKBOX_CLASS =
  "size-[18px] shrink-0 cursor-pointer appearance-none rounded-[3px] border-2 border-nav-active bg-white " +
  "checked:bg-nav-active checked:border-nav-active relative " +
  "checked:after:content-['✓'] checked:after:absolute checked:after:inset-0 checked:after:flex " +
  "checked:after:items-center checked:after:justify-center checked:after:text-white " +
  "checked:after:text-[12px] checked:after:font-bold checked:after:leading-none " +
  "transition-colors";

function AlertRow({ event, selected, onToggleSelected, onAcknowledge, onResolve }) {
  const status = event.my_status || "new";
  const stop = (handler) => (e) => { e.stopPropagation(); handler(event.id); };
  return (
    <div
      onClick={() => onToggleSelected(event.id)}
      className="group relative bg-card-bg rounded-[5px] px-5 py-3 cursor-pointer transition-shadow hover:shadow-[0_14px_42px_0_var(--shadow-blue-lg)] hover:z-10 flex flex-col md:flex-row md:items-start gap-3 md:gap-4"
    >
      <div className="flex items-start gap-4 flex-1 min-w-0">
        <input
          type="checkbox"
          checked={selected}
          onChange={() => onToggleSelected(event.id)}
          onClick={(e) => e.stopPropagation()}
          className={`mt-1 ${CHECKBOX_CLASS}`}
        />
        <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <SeverityPill severity={event.severity} />
          {event.rule_name && (
            <span className="text-xs text-[var(--page-muted,#6b7280)]">{event.rule_name}</span>
          )}
          <span className="text-xs text-[var(--page-muted,#6b7280)]">· {formatTime(event.triggered_at)}</span>
          {status !== "new" && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-[var(--table-stripe,#f3f4f6)] text-[var(--page-muted,#6b7280)]">
              {status}
            </span>
          )}
        </div>
        <div className="text-base font-medium text-[var(--page-title,#111)] mb-0.5 truncate">{event.title}</div>
        {event.message && (
          <div className="text-sm text-[var(--page-muted,#4b5563)]">{event.message}</div>
        )}
        {event.target_type && (
          <div className="text-xs text-[var(--page-muted,#6b7280)] mt-1">
            {event.target_type}{event.target_id ? `: ${event.target_id}` : ""}
          </div>
        )}
        </div>
      </div>
      <div className="flex flex-row md:flex-col gap-2 shrink-0 flex-wrap justify-end">
        {status === "new" && (
          <button
            type="button"
            onClick={stop(onAcknowledge)}
            className="flex items-center gap-1.5 h-8 px-3 rounded-[5px] border-2 border-nav-active text-nav-active bg-white text-sm font-medium cursor-pointer hover:border-nav-active-hover hover:text-nav-active-hover transition-colors"
          >
            <Check size={16} strokeWidth={2.5} /> Acknowledge
          </button>
        )}
        {status !== "resolved" && (
          <button
            type="button"
            onClick={stop(onResolve)}
            className="flex items-center gap-1.5 h-8 px-3 rounded-[5px] border-2 border-nav-active text-nav-active bg-white text-sm font-medium cursor-pointer hover:border-nav-active-hover hover:text-nav-active-hover transition-colors"
          >
            <CheckCheck size={16} strokeWidth={2.5} /> Resolve
          </button>
        )}
      </div>
    </div>
  );
}

export default function Alerts() {
  const { confirm } = useConfirm();
  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("new");
  const [severityFilter, setSeverityFilter] = useState("all");
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter && statusFilter !== "all") params.status = statusFilter;
      if (severityFilter && severityFilter !== "all") params.severity = severityFilter;
      const res = await fetchAlertsFeed(params);
      setEvents(res?.data || []);
      setTotal(res?.total || 0);
    } catch (err) {
      console.error("Failed to load alerts", err);
      setEvents([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, severityFilter]);

  useEffect(() => {
    load();
    const intervalId = setInterval(load, 30000);
    return () => clearInterval(intervalId);
  }, [load]);

  const handleAcknowledge = async (id) => {
    try {
      await acknowledgeAlert(id);
      await load();
    } catch (e) { console.error(e); }
  };
  const handleResolve = async (id) => {
    try {
      await resolveAlert(id);
      await load();
    } catch (e) { console.error(e); }
  };

  const toggleSelected = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const visibleIds = useMemo(() => events.map(e => e.id), [events]);
  const allSelected = visibleIds.length > 0 && visibleIds.every(id => selectedIds.has(id));
  const someSelected = selectedIds.size > 0 && !allSelected;

  const toggleSelectAll = () => {
    setSelectedIds(prev => {
      if (allSelected) return new Set();
      const next = new Set(prev);
      visibleIds.forEach(id => next.add(id));
      return next;
    });
  };

  const handleBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (!(await confirm({ title: `Delete ${ids.length} alert${ids.length === 1 ? "" : "s"}?`, description: "This permanently removes the selected alert(s).", confirmText: "Delete" }))) return;
    try {
      await deleteAlerts(ids);
      setSelectedIds(new Set());
      await load();
    } catch (e) { console.error(e); }
  };

  return (
    <div className="pt-[30px] pb-24 md:pb-[30px] px-4 md:px-[60px] bg-page-bg min-h-full flex flex-col gap-4">
      <div>
        <h1 className="text-[32px] font-medium leading-tight text-page-title">Alerts</h1>
        <p className="text-sm font-normal text-page-muted">
          {loading ? "Loading…" : `${total} ${total === 1 ? "alert" : "alerts"}`}
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="inline-flex border-2 border-[#377AD6] rounded-[5px] overflow-hidden">
          {STATUS_TABS.map(({ id, label }, idx) => (
            <button
              key={id}
              onClick={() => setStatusFilter(id)}
              className={[
                'flex items-center justify-center gap-1.5 h-[30px] w-[120px] text-xs font-medium cursor-pointer border-0 outline-none transition-colors',
                idx < STATUS_TABS.length - 1 ? '[border-right:2px_solid_#377AD6]' : '',
                statusFilter === id ? 'bg-[#377AD6] text-white' : 'bg-card-bg text-[#377AD6] hover:bg-[#377AD6]/5'
              ].filter(Boolean).join(' ')}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="ml-auto">
        <FilterSelect value={severityFilter} onValueChange={setSeverityFilter} placeholder="Severity">
          <FilterSelectItem value="all">All severities</FilterSelectItem>
          <FilterSelectItem value="critical">Critical</FilterSelectItem>
          <FilterSelectItem value="warning">Warning</FilterSelectItem>
          <FilterSelectItem value="info">Info</FilterSelectItem>
        </FilterSelect>
        </div>
      </div>

      {events.length > 0 && (
        <div className="flex items-center gap-3 px-5 py-2 bg-card-bg rounded-[5px]">
          <input
            type="checkbox"
            checked={allSelected}
            ref={el => { if (el) el.indeterminate = someSelected; }}
            onChange={toggleSelectAll}
            className={CHECKBOX_CLASS}
          />
          <span className="text-sm text-page-muted">
            {selectedIds.size > 0 ? `${selectedIds.size} selected` : "Select all"}
          </span>
          <button
            type="button"
            onClick={handleBulkDelete}
            disabled={selectedIds.size === 0}
            className="ml-auto flex items-center gap-1.5 h-8 px-3 rounded-[5px] border-2 border-red-600 text-red-600 bg-white text-sm font-medium cursor-pointer hover:border-nav-active-hover hover:text-nav-active-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-red-600 disabled:hover:text-red-600"
          >
            <Trash2 size={16} strokeWidth={2.5} />
            Delete selected{selectedIds.size > 0 ? ` (${selectedIds.size})` : ""}
          </button>
        </div>
      )}

      <div className="flex flex-col gap-3">
        {!loading && events.length === 0 && (
          <div className="text-sm text-[var(--page-muted,#6b7280)] py-12 text-center">
            No alerts to show.
          </div>
        )}
        {events.map((e) => (
          <AlertRow
            key={e.id}
            event={e}
            selected={selectedIds.has(e.id)}
            onToggleSelected={toggleSelected}
            onAcknowledge={handleAcknowledge}
            onResolve={handleResolve}
          />
        ))}
      </div>
    </div>
  );
}
